import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiChevronDown, FiArrowUpRight } from 'react-icons/fi';
import Reveal from './Reveal';
import Footer from './Footer';

const sections = [
  {
    question: 'What makes Lotez suitable for software development?',
    content:
      "We combine technical expertise, a tailored approach, modern technologies and a user-centered design philosophy to deliver custom software that meets your needs today and is built to scale for tomorrow's challenges.",
  },
  {
    question: 'How can Lotez improve your product growth?',
    content:
      'By delivering solutions aligned with your goals, optimising user experiences and driving continuous improvement through data-driven insight. Our collaborative approach keeps technology aligned with your vision so you have the tools and support to succeed long-term.',
  },
  {
    question: 'Why choose Lotez for your business needs?',
    content:
      'Lotez provides cost-effective, scalable and customisable options to fit businesses of all sizes and industries.',
  },
  {
    question: 'What features does Lotez offer?',
    content:
      'A full suite covering custom software and mobile development, AI integration, digital marketing and cloud solutions — end-to-end services to help your business grow, adapt and thrive.',
  },
];

const Modal = ({ title, body, onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 backdrop-blur-sm px-4">
    <div className="glass-card w-full max-w-sm rounded-3xl p-8 text-center">
      <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
      <p className="mt-3 text-sm text-muted">{body}</p>
      <button className="btn-primary mt-6 w-full" onClick={onClose}>
        Got it
      </button>
    </div>
  </div>
);

const CONTACT_EMAIL = 'hello@lotez.in';

const Contact = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  const toggleAccordion = (index) =>
    setActiveIndex(activeIndex === index ? null : index);

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = document.getElementById('name').value.trim();
    const email = document.getElementById('email').value.trim();
    const message = document.getElementById('message').value.trim();

    if (!name || !email || !message) {
      setDetailsError(true);
      return;
    }

    const subject = `New Contact Form Submission from ${name}`;
    const body = `Name: ${name}\nEmail: ${email}\n\n${message}`;
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;
    setShowModal(true);
    e.target.reset();
  };

  const fieldClass =
    'w-full rounded-2xl border border-line bg-white/70 px-4 py-3 text-sm text-ink placeholder:text-muted/60 transition focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25';

  return (
    <div>
      {/* Hero + form */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-radial-mint" />
        <div className="container-x relative pt-36 pb-12 text-center sm:pt-44">
          <Reveal>
            <span className="section-kicker">Contact us</span>
            <h1 className="mt-4 font-display text-4xl font-bold text-ink sm:text-5xl">
              Let's build something{' '}
              <span className="text-gradient">together</span>
            </h1>
            <p className="mx-auto mt-5 max-w-lg text-muted">
              Drop us a message and let's start a journey to elevate your
              brand's digital experience.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={handleSubmit}
              className="glass-card mx-auto mt-12 max-w-xl rounded-4xl p-6 text-left sm:p-8"
            >
              <div className="grid gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted"
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-muted"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    placeholder="Tell us about your project..."
                    className={`${fieldClass} resize-none`}
                  />
                </div>
                <button type="submit" className="btn-primary w-full">
                  Open in email app <FiArrowUpRight className="text-base" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {showModal && (
        <Modal
          title="Almost there"
          body="Your email app should now be open with your message ready — just hit send."
          onClose={() => setShowModal(false)}
        />
      )}
      {detailsError && (
        <Modal
          title="Details required"
          body="Please fill in all fields before submitting."
          onClose={() => setDetailsError(false)}
        />
      )}

      {/* FAQ */}
      <section className="section">
        <div className="container-x grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <span className="section-kicker">FAQ</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
              Frequently asked <span className="text-gradient">questions</span>
            </h2>
            <p className="mt-4 text-muted">
              Quick answers about our services, process and the way we work.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass-card divide-y divide-line overflow-hidden rounded-4xl">
              {sections.map((section, index) => {
                const open = activeIndex === index;
                return (
                  <div key={index}>
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="font-display text-base font-semibold text-ink">
                        {section.question}
                      </span>
                      <FiChevronDown
                        className={`shrink-0 text-lg text-brand transition-transform duration-300 ${
                          open ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <p className="px-6 pb-6 text-sm leading-relaxed text-muted">
                            {section.content}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
